import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import MarkdownReader from "@/components/affiliate/MarkdownReader";
import ProtocolCard from "@/components/injection/ProtocolCard";
import { getFoundations, getProtocol, getOnboarding, markOnboardingStep, completeOnboarding } from "@/lib/injectionApi";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

const TOTAL = 5;

// Five-step onboarding flow shown to members after they're approved
// and their first kit ships. Skippable, resumable, revisitable from
// /app/me. BE drives `state.currentStep` so members resume in place
// across devices; we mirror locally in case the BE write fails.
export default function InjectionOnboarding() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [step, setStep] = useState(Math.max(1, Math.min(TOTAL, parseInt(params.get("step") || "1", 10) || 1)));
  const [state, setState] = useState(null);
  const [foundations, setFoundations] = useState(null);
  const [protocol, setProtocol] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, f, p] = await Promise.all([
          getOnboarding().catch(() => null),
          getFoundations().catch(() => null),
          getProtocol().catch(() => null),
        ]);
        setState(s || {});
        setFoundations(f);
        setProtocol(p);
        if (s?.currentStep && !params.get("step")) {
          setStep(Math.max(1, Math.min(TOTAL, s.currentStep)));
        }
      } catch {
        setState({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("step", String(step));
      return next;
    }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, setParams]);

  const advance = async () => {
    try { await markOnboardingStep(step); } catch { /* silent — local nav still moves */ }
    if (step >= TOTAL) {
      try { await completeOnboarding(); } catch { /* silent */ }
      toast("Onboarding complete — you're ready for your first injection.", "success");
      navigate("/app");
      return;
    }
    setStep(step + 1);
  };

  const skip = () => {
    toast("Saved — you can pick this up anytime from your dashboard.", "info");
    navigate("/app");
  };

  if (state === null) return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;

  return (
    <div data-testid="injection-onboarding" style={{ minHeight: "100vh", background: C.cream, color: C.ink, display: "flex", flexDirection: "column" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: `1px solid ${C.warmGray3}` }}>
        <Logo size={20} />
        <button
          type="button"
          onClick={skip}
          data-testid="onboarding-skip"
          style={{ background: "transparent", border: 0, color: C.warmGray, fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", cursor: "pointer" }}
        >
          SKIP FOR NOW ✕
        </button>
      </header>

      {/* Progress strip */}
      <div data-testid="onboarding-progress" style={{ display: "flex", gap: 6, padding: "14px 24px", background: C.bone, borderBottom: `1px solid ${C.warmGray3}` }}>
        {Array.from({ length: TOTAL }).map((_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 4,
              background: i < step ? C.peachDeep : "rgba(122,107,93,0.18)",
              transition: "background 200ms",
            }}
          />
        ))}
      </div>

      <main style={{ flex: 1, padding: "32px 24px 80px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          {step === 1 && <StepWelcome />}
          {step === 2 && <StepFoundations foundations={foundations} />}
          {step === 3 && <StepProtocol protocol={protocol} />}
          {step === 4 && <StepFirstInjection />}
          {step === 5 && <StepCareTeam protocol={protocol} />}
        </div>
      </main>

      <footer style={{ borderTop: `1px solid ${C.warmGray3}`, padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", background: C.cream, position: "sticky", bottom: 0 }}>
        {step > 1 ? (
          <button
            type="button"
            onClick={() => setStep(step - 1)}
            data-testid="onboarding-back"
            style={{ background: "transparent", border: 0, color: C.warmGray, fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", cursor: "pointer" }}
          >
            ← BACK
          </button>
        ) : <span />}
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray }}>
          STEP {String(step).padStart(2, "0")} / 0{TOTAL}
        </span>
        <Button variant="primary" size="lg" testId="onboarding-next" onClick={advance}>
          {step === TOTAL ? "FINISH →" : "CONTINUE →"}
        </Button>
      </footer>
    </div>
  );
}

function StepWelcome() {
  return (
    <div>
      <Eyebrow color={C.peachDeep}>★ A FEW MINUTES BEFORE YOUR KIT ARRIVES</Eyebrow>
      <Headline before="WELCOME TO YOUR" italic="protocol." size={52} className="mt-6" />
      <p style={{ marginTop: 24, fontSize: 17, lineHeight: 1.65, color: C.ink, maxWidth: 580 }}>
        Your kit is on the way. Before it lands, we're going to walk you through five things — slowly, carefully, in the order they matter.
      </p>
      <ul style={{ marginTop: 24, paddingLeft: 22, fontSize: 15, lineHeight: 1.8, color: C.ink, maxWidth: 580 }}>
        <li>The injection foundations — written for first-timers</li>
        <li>Your specific protocol card — compound, dose, draw volume</li>
        <li>What to do the day your kit arrives</li>
        <li>How to reach your care team — no question is too small</li>
      </ul>
      <p style={{ marginTop: 22, fontSize: 13, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", lineHeight: 1.6 }}>
        Take it at your own pace. We save your progress as you go, and you can come back to any section anytime from your dashboard.
      </p>
    </div>
  );
}

function StepFoundations({ foundations }) {
  return (
    <div>
      <Eyebrow color={C.peachDeep}>★ STEP 02 — READ FIRST</Eyebrow>
      <Headline before="INJECTION" italic="foundations." size={48} className="mt-6" />
      <p style={{ marginTop: 16, fontSize: 15, color: C.warmGray, lineHeight: 1.65, maxWidth: 580 }}>
        Read this end-to-end before your first injection. Re-read it the night before. It covers reconstitution, drawing your dose, technique, storage, and when to call us.
      </p>
      <div style={{ marginTop: 32, background: C.pure, padding: 28, border: `1px solid ${C.warmGray3}` }}>
        {foundations?.body_md ? (
          <MarkdownReader source={foundations.body_md} testId="onboarding-foundations" />
        ) : (
          <div data-testid="foundations-empty" style={{ fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            We're loading the Foundations guide for you — refresh in a few moments if it doesn't appear.
          </div>
        )}
      </div>
    </div>
  );
}

function StepProtocol({ protocol }) {
  return (
    <div>
      <Eyebrow color={C.peachDeep}>★ STEP 03 — YOUR NUMBERS</Eyebrow>
      <Headline before="YOUR PROTOCOL" italic="card." size={48} className="mt-6" />
      <p style={{ marginTop: 16, fontSize: 15, color: C.warmGray, lineHeight: 1.65, maxWidth: 580 }}>
        This is the card you'll come back to every injection. Compound, dose, exact reconstitution math, draw volume on the syringe. Save it. Print it. Tape it to your kit.
      </p>
      <div style={{ marginTop: 24 }}>
        <ProtocolCard protocol={protocol} testId="onboarding-protocol-card" />
      </div>
    </div>
  );
}

function StepFirstInjection() {
  const items = [
    { title: "When your kit arrives", body: "Unbox in a clean space. Check that compound, bacteriostatic water, syringes, and the sharps container are all present." },
    { title: "Read the Foundations once more", body: "Skim the reconstitution and draw sections the night before — you'll feel the difference." },
    { title: "Schedule a calm moment", body: "First injections feel easier when you're not rushed. Pick a quiet 20 minutes." },
    { title: "Set up your surface", body: "Clean countertop, alcohol wipes within reach, the protocol card open in front of you." },
    { title: "Take your time", body: "Slow reconstitution, slow draw, slow injection. Speed comes after a few weeks of practice." },
  ];
  return (
    <div>
      <Eyebrow color={C.peachDeep}>★ STEP 04 — DAY-OF</Eyebrow>
      <Headline before="WHEN YOUR KIT" italic="arrives." size={48} className="mt-6" />
      <p style={{ marginTop: 16, fontSize: 15, color: C.warmGray, lineHeight: 1.65, maxWidth: 580 }}>
        A short prep checklist so the day-of feels familiar, not surprising.
      </p>
      <ol style={{ marginTop: 28, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 14 }}>
        {items.map((it, i) => (
          <li
            key={i}
            data-testid={`prep-item-${i + 1}`}
            style={{ background: C.pure, border: `1px solid ${C.warmGray3}`, padding: 18, display: "flex", gap: 14, alignItems: "flex-start" }}
          >
            <div style={{ width: 28, height: 28, background: C.peach, color: C.ink, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
              {i + 1}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{it.title}</div>
              <div style={{ marginTop: 4, fontSize: 14, lineHeight: 1.6, color: C.warmGray }}>{it.body}</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function StepCareTeam({ protocol }) {
  return (
    <div>
      <Eyebrow color={C.peachDeep}>★ STEP 05 — WE'RE HERE</Eyebrow>
      <Headline before="YOUR CARE TEAM" italic="is here." size={48} className="mt-6" />
      <p style={{ marginTop: 16, fontSize: 15, color: C.warmGray, lineHeight: 1.65, maxWidth: 580 }}>
        You're not figuring this out alone. Anything that surprises you, anything that feels off — we want to hear about it.
      </p>
      <div style={{ marginTop: 28, background: C.ink, color: C.cream, padding: 28 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peach }}>★ HOW TO REACH US</div>
        <ul style={{ marginTop: 14, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 14 }}>
          <li>
            <strong style={{ display: "block", fontSize: 14, color: C.cream }}>Message us in-app</strong>
            <span style={{ fontSize: 13, color: "rgba(250,246,240,0.7)" }}>From the Care Team tab on your dashboard. Usually answered within a few business hours.</span>
          </li>
          {protocol?.careTeamPhone && (
            <li>
              <strong style={{ display: "block", fontSize: 14, color: C.cream }}>Call {protocol.careTeamPhone}</strong>
              <span style={{ fontSize: 13, color: "rgba(250,246,240,0.7)" }}>For anything that can't wait.</span>
            </li>
          )}
          <li>
            <strong style={{ display: "block", fontSize: 14, color: C.coral }}>For true emergencies — call 911 or go to the ER</strong>
            <span style={{ fontSize: 13, color: "rgba(250,246,240,0.7)" }}>Severe allergic reactions, signs of serious infection, or anything else that needs immediate care.</span>
          </li>
        </ul>
      </div>
      <p style={{ marginTop: 18, fontSize: 13, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", lineHeight: 1.6 }}>
        No question is too small. If something in the guide didn't make sense, message us before you inject.
      </p>
    </div>
  );
}
